import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Fade,
  Grid,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCourse } from '../store/thunks/coursesThunks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BookIcon from '@mui/icons-material/Book';
import { CourseSection } from '@e-learning/shared/src/store/types';
import MarkdownRenderer from '../components/MarkdownRenderer';
import AdSenseBanner from '../components/AdSenseBanner';

const CourseContentPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedCourse, loading, error } = useAppSelector((state) => state.courses);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  // Check for section parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sectionParam = params.get('section');
    if (sectionParam) {
      const sectionIndex = parseInt(sectionParam, 10);
      if (!isNaN(sectionIndex) && sectionIndex >= 0) {
        setCurrentSectionIndex(sectionIndex);
      }
    }
  }, []);

  useEffect(() => {
    if (courseId && (!selectedCourse || selectedCourse.id !== courseId)) {
      dispatch(fetchCourse(courseId));
    }
  }, [courseId, dispatch, selectedCourse]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !selectedCourse) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error || 'Course not found'}</Alert>
          <Button onClick={() => navigate('/courses')} sx={{ mt: 2 }}>
            Back to Courses
          </Button>
        </Box>
      </Container>
    );
  }

  const sections = selectedCourse.sections || [];
  const sortedSections = [...sections].sort((a, b) => a.order - b.order);
  const currentSection: CourseSection | null = sortedSections[currentSectionIndex] || null;
  const totalSections = sortedSections.length;

  const handlePrevious = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleNext = () => {
    if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBackToDetail = () => {
    navigate(`/courses/${courseId}`);
  };

  if (totalSections === 0) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="info">This course has no content sections yet.</Alert>
          <Button onClick={handleBackToDetail} sx={{ mt: 2 }}>
            Back to Course Details
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 2, md: 3 } }}>
      <Grid container spacing={3}>
        {/* Left AdSense Banner */}
        <Grid item xs={0} lg={2} sx={{ display: { xs: 'none', lg: 'block' } }}>
          <AdSenseBanner
            slot="1234567890"
            format="vertical"
            position="left"
          />
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ my: 4 }}>
            <Fade in timeout={500}>
              <Button
                onClick={handleBackToDetail}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateX(-4px)',
                  },
                }}
              >
                ← Back to Course Details
              </Button>
            </Fade>

        <Fade in timeout={600}>
          <Box
            sx={{
              mb: 4,
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
              border: '1px solid rgba(148, 163, 184, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {selectedCourse.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentSection?.title}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                background: 'rgba(99, 102, 241, 0.2)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Section {currentSectionIndex + 1} of {totalSections}
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Fade in timeout={700}>
          <Paper
            elevation={0}
            sx={{
              p: 5,
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
              border: '1px solid rgba(148, 163, 184, 0.1)',
              minHeight: '400px',
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                mb: 3,
                pb: 2,
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                fontWeight: 600,
              }}
            >
              {currentSection?.title}
            </Typography>
            <Divider sx={{ mb: 4 }} />
            {currentSection?.content && (
              <MarkdownRenderer content={currentSection.content} />
            )}
          </Paper>
        </Fade>

        <Fade in timeout={800}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 4,
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handlePrevious}
              disabled={currentSectionIndex === 0}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: 'rgba(148, 163, 184, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover:not(:disabled)': {
                  borderColor: 'primary.main',
                  background: 'rgba(99, 102, 241, 0.1)',
                  transform: 'translateX(-4px)',
                },
                '&:disabled': {
                  opacity: 0.5,
                },
              }}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              endIcon={currentSectionIndex === totalSections - 1 ? undefined : <ArrowForwardIcon />}
              onClick={currentSectionIndex === totalSections - 1 ? handleBackToDetail : handleNext}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 8px 16px rgba(99, 102, 241, 0.4)',
                },
              }}
            >
              {currentSectionIndex === totalSections - 1 ? 'Complete Course' : 'Next'}
            </Button>
          </Box>
        </Fade>
          </Box>
        </Grid>

        {/* Right AdSense Banner */}
        <Grid item xs={0} lg={2} sx={{ display: { xs: 'none', lg: 'block' } }}>
          <AdSenseBanner
            slot="0987654321"
            format="vertical"
            position="right"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CourseContentPage;

